import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import { alpha } from '@mui/material/styles';

import { useAlert } from '../../context/AlertContext';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { CustomeDateTimePicker } from '../../core/CustomeDateTimePicker';
import { ApiError } from '../../hooks/useFetch';
import {
  IMeetingRequest,
  MEETING_REQUEST_STATUS,
} from '../../Interfaces/IMeetingRequest';
import {
  useAcceptMeetingRequest,
  useCounterMeetingRequest,
  useGetBookingOptions,
  useGetLegalTransactionMeetingRequests,
  useRejectMeetingRequest,
} from '../../hooks/reactQuerys/MeetingRequestQueries';
import { useGetLocations } from '../../hooks/reactQuerys/LocationQueries';
import { useGetRooms } from '../../hooks/reactQuerys/RoomQueries';
import { MeetingRequestMailEditor } from './MeetingRequestMailEditor';
import { CounterProposalSlotPicker } from './CounterProposalSlotPicker';

/** Pro-Mandant-Vorgabe der gezielten Terminaufforderung. Enthaltene Mandanten
 *  gelten als ausgewählt (bekommen Mail + sehen CTA/Reiter im Portal). */
export interface IRequestMeetingClientConfig {
  idLegalClient: number;
  /** Vorgegebene Terminart (null = Mandant wählt selbst). */
  idMeetingType: number | null;
  /** Vorgegebener Standort (null = frei wählbar, falls mehrere). */
  idLocation: number | null;
  /** Notiz an den Mandanten (im Portal angezeigt). */
  note: string;
}

interface IProps {
  idLegalTransaction: number;
  /** Aktueller Stand des Flags „Mandant soll Termin vereinbaren" (aus dem Vorgang). */
  requestClientMeeting?: boolean;
  /** Ändert das Flag im Vorgangs-State. Persistiert + synct erst beim
   *  Speichern des Vorgangs (kein sofortiges „hot" Speichern). */
  onRequestClientMeetingChange?: (value: boolean) => void;
  /** Mandanten des Vorgangs (für die pro-Mandant-Konfiguration). */
  legalClients?: ILegalClient[];
  /** Pro-Mandant-Konfiguration (wer aufgefordert wird + Terminart/Standort/Notiz). */
  requestMeetingClientConfig?: IRequestMeetingClientConfig[];
  /** Ändert die pro-Mandant-Konfiguration im Vorgangs-State (Versand beim Speichern). */
  onRequestMeetingClientConfigChange?: (
    config: IRequestMeetingClientConfig[]
  ) => void;
}

interface IStatusInfo {
  label: string;
  color: AlertColor | 'default';
}

const STATUS_INFO: Record<number, IStatusInfo> = {
  [MEETING_REQUEST_STATUS.REQUESTED]: { label: 'Angefragt', color: 'warning' },
  [MEETING_REQUEST_STATUS.ACCEPTED]: { label: 'Angenommen', color: 'success' },
  [MEETING_REQUEST_STATUS.REJECTED]: { label: 'Abgelehnt', color: 'error' },
  [MEETING_REQUEST_STATUS.COUNTER_PROPOSED]: {
    label: 'Gegenvorschlag – wartet auf Mandant',
    color: 'info',
  },
  [MEETING_REQUEST_STATUS.CANCELLED]: { label: 'Storniert', color: 'default' },
  [MEETING_REQUEST_STATUS.EXPIRED]: { label: 'Abgelaufen', color: 'default' },
};

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '–';
  }
  // Termin-/Anfragezeiten sind naive Europe/Berlin-Wandzeit ("YYYY-MM-DD HH:MM:SS").
  // Als lokale Zeit parsen (konsistent zum Mandantenportal) — NICHT als UTC, sonst
  // zeigt das Notariat eine um den TZ-Offset verschobene Uhrzeit.
  const date = new Date(value.replace(' ', 'T'));
  if (isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('de-DE', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

/** Backend-Fehlermeldung beim Annehmen verständlich aufbereiten. Das Backend
 *  liefert die konkrete Ursache als {error: "..."} (400 falscher/fehlender Raum,
 *  409 Raum belegt bzw. Slot nicht mehr verfügbar). */
const acceptErrorText = (error: unknown): string => {
  if (error instanceof ApiError) {
    const detail = (error.body as { error?: unknown } | null)?.error;
    if (typeof detail === 'string' && detail.trim() !== '') {
      return detail;
    }
    if (error.status === 409) {
      return 'Der Raum bzw. der gewünschte Termin ist nicht mehr verfügbar.';
    }
    if (error.status === 400) {
      return 'Bitte einen gültigen Raum aus dem Standort auswählen.';
    }
  }
  return 'Annehmen fehlgeschlagen (Slot evtl. nicht mehr frei).';
};

type DialogMode = 'reject' | 'counter' | 'accept';

export const LegalTransactionMeetingRequestPanel: React.FC<IProps> = ({
  idLegalTransaction,
  requestClientMeeting = false,
  onRequestClientMeetingChange,
  legalClients = [],
  requestMeetingClientConfig = [],
  onRequestMeetingClientConfigChange,
}) => {
  const { showAlert } = useAlert();
  const { meetingRequests, isLoadingMeetingRequests, refetchMeetingRequests } =
    useGetLegalTransactionMeetingRequests(idLegalTransaction);
  const { acceptMeetingRequest, isAccepting } = useAcceptMeetingRequest();
  const { rejectMeetingRequest, isRejecting } = useRejectMeetingRequest();
  const { counterMeetingRequest, isCountering } = useCounterMeetingRequest();
  const { locations } = useGetLocations();
  const { rooms } = useGetRooms();
  // Zentrale buchbare Optionen: EINZIGE Verfügbarkeits-Wahrheit für die Kopplung
  // Terminart↔Standort und die Aktivierungssperre.
  const { bookingOptions } = useGetBookingOptions();

  const bookableTypeIds = new Set(bookingOptions.map(o => o.idMeetingType));
  const bookableLocationsForType = (idMeetingType: number | null) =>
    idMeetingType == null
      ? []
      : (bookingOptions.find(o => o.idMeetingType === idMeetingType)
          ?.locations ?? []);
  const nothingBookable = bookingOptions.length === 0;

  // Pro-Mandant-Konfiguration als Map (idLegalClient -> Vorgabe) für schnellen Zugriff.
  const configByClient = new Map<number, IRequestMeetingClientConfig>();
  requestMeetingClientConfig.forEach(c =>
    configByClient.set(c.idLegalClient, c)
  );

  const emitConfig = (next: IRequestMeetingClientConfig[]) =>
    onRequestMeetingClientConfigChange?.(next);

  // Mandant an-/abwählen (Haken). Beim Anhaken mit leeren Vorgaben starten.
  const toggleClient = (idLegalClient: number, checked: boolean) => {
    if (checked) {
      if (configByClient.has(idLegalClient)) return;
      emitConfig([
        ...requestMeetingClientConfig,
        { idLegalClient, idMeetingType: null, idLocation: null, note: '' },
      ]);
    } else {
      emitConfig(
        requestMeetingClientConfig.filter(
          c => c.idLegalClient !== idLegalClient
        )
      );
    }
  };

  // Ein Feld der Vorgabe eines Mandanten ändern.
  const patchClient = (
    idLegalClient: number,
    patch: Partial<IRequestMeetingClientConfig>
  ) =>
    emitConfig(
      requestMeetingClientConfig.map(c =>
        c.idLegalClient === idLegalClient ? { ...c, ...patch } : c
      )
    );

  // Flag kann aus dem Backend als Zahl (0/1) kommen -> hart zu Boolean machen,
  // sonst rendert `{0 && ...}` eine sichtbare „0" im JSX.
  const clientMeetingActive = Boolean(requestClientMeeting);

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [activeRequest, setActiveRequest] = useState<IMeetingRequest | null>(
    null
  );
  const [counterStart, setCounterStart] = useState<string | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<number | ''>('');
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');
  // Interne Notiz (nur intern; wird am Request gespeichert und in der Karte
  // angezeigt, geht NICHT an den Mandanten).
  const [staffMessage, setStaffMessage] = useState('');
  // Annehmen ist zweistufig: 1 = Raum/Standort, 2 = Mail bearbeiten.
  const [acceptStep, setAcceptStep] = useState<1 | 2>(1);
  // Koordination der Mit-Beteiligten beim Annehmen: pro weiterem Mandant des
  // Vorgangs waehlbar, ob er als Teilnehmer den Termin + Bestaetigung/iCal
  // bekommt. Leere Auswahl = Bestaetigung nur an den anfragenden Mandanten.
  const [sharedClientIds, setSharedClientIds] = useState<number[]>([]);
  // Kalender-Konflikt beim Annehmen (Backend 409 CALENDAR_CONFLICT): prominent
  // als persistente Alert im Dialog anzeigen (nicht nur als Toast), bis eine
  // erneute Annahme klappt oder der Dialog geschlossen wird.
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const busy = isAccepting || isRejecting || isCountering;

  const locationName = (idLocation?: number | null): string | null => {
    if (!idLocation) {
      return null;
    }
    return (
      locations?.find(location => location.idLocation === idLocation)
        ?.Location ?? null
    );
  };

  const roomName = (idRoom?: number | null): string | null => {
    if (!idRoom) {
      return null;
    }
    // Soft-gelöschte Räume sind nicht mehr in der Liste — den Raum eines
    // bestehenden Termins trotzdem nicht wortlos verschwinden lassen.
    return (
      rooms.find(room => room.idRoom === idRoom)?.Name ?? 'Raum (entfernt)'
    );
  };

  // Räume des am Request gewünschten Standorts (Pflichtauswahl beim Annehmen).
  const roomsForActiveRequest = activeRequest?.idLocation
    ? rooms.filter(
        room => room.Location_idLocation === activeRequest.idLocation
      )
    : [];

  const clientName = (id?: number | null): string | null => {
    if (!id) return null;
    const c = legalClients.find(lc => lc.idLegalClient === id);
    return c
      ? [c.FirstName, c.LastName].filter(Boolean).join(' ') || `#${id}`
      : `#${id}`;
  };

  // Alle WEITEREN Mandanten des Vorgangs (unabhängig davon, ob sie zum
  // Terminvereinbaren aufgefordert wurden) — für die Koordination beim
  // Annehmen: pro Mandant wählbar, ob er den Termin ebenfalls bekommt.
  const otherClients = activeRequest
    ? legalClients.filter(c => c.idLegalClient !== activeRequest.idLegalClient)
    : [];

  const toggleSharedClient = (id: number) =>
    setSharedClientIds(current =>
      current.includes(id) ? current.filter(x => x !== id) : [...current, id]
    );

  const closeDialog = () => {
    setDialogMode(null);
    setActiveRequest(null);
    setCounterStart(null);
    setSelectedRoom('');
    setMailSubject('');
    setMailBody('');
    setStaffMessage('');
    setAcceptStep(1);
    setSharedClientIds([]);
    setConflictMessage(null);
  };

  const openDialog = (mode: DialogMode, request: IMeetingRequest) => {
    setActiveRequest(request);
    setCounterStart(null);
    setSelectedRoom('');
    setMailSubject('');
    setMailBody('');
    setStaffMessage('');
    setAcceptStep(1);
    setSharedClientIds([]);
    setConflictMessage(null);
    setDialogMode(mode);
  };

  // Annehmen läuft immer über den Dialog, damit die Bestätigungs-Mail vor dem
  // Versand bearbeitet werden kann. Mit Standort: Schritt 1 = Raum-Pflichtwahl,
  // dann Schritt 2 = Mail. Ohne Standort: direkt Schritt 2 (kein Raum nötig).
  const onAcceptClick = (request: IMeetingRequest) => {
    openDialog('accept', request);
    setAcceptStep(request.idLocation ? 1 : 2);
  };

  const handleAcceptWithRoom = async () => {
    if (!activeRequest) {
      return;
    }
    // Raum nur erforderlich, wenn ein Standort gewählt ist.
    if (activeRequest.idLocation && selectedRoom === '') {
      return;
    }
    setConflictMessage(null);
    try {
      await acceptMeetingRequest({
        idMeetingRequest: activeRequest.idLegalTransactionMeetingRequest,
        ...(activeRequest.idLocation
          ? { idLocation: activeRequest.idLocation }
          : {}),
        ...(selectedRoom !== '' ? { idRoom: selectedRoom } : {}),
        staffMessage: staffMessage.trim() || undefined,
        subject: mailSubject,
        body: mailBody,
        // Ausgewählte weitere Mandanten werden Teilnehmer am selben Termin und
        // bekommen die Bestätigung inkl. iCal (mode 'shared'). Nichts gewählt =
        // keine Koordination, Bestätigung nur an den anfragenden Mandanten.
        ...(sharedClientIds.length > 0
          ? {
              coordination: {
                mode: 'shared' as const,
                otherClientIds: sharedClientIds,
                informOthers: true,
              },
            }
          : {}),
      });
      showAlert({ severity: 'success', text: 'Terminanfrage angenommen.' });
      closeDialog();
      void refetchMeetingRequests();
    } catch (error) {
      if (
        error instanceof ApiError &&
        (error.body as { code?: string } | null)?.code === 'CALENDAR_CONFLICT'
      ) {
        setConflictMessage(acceptErrorText(error));
      }
      showAlert({ severity: 'error', text: acceptErrorText(error) });
    }
  };

  const handleReject = async () => {
    if (!activeRequest) {
      return;
    }
    try {
      await rejectMeetingRequest({
        idMeetingRequest: activeRequest.idLegalTransactionMeetingRequest,
        staffMessage: staffMessage.trim() || undefined,
        subject: mailSubject,
        body: mailBody,
      });
      showAlert({ severity: 'success', text: 'Terminanfrage abgelehnt.' });
      closeDialog();
      void refetchMeetingRequests();
    } catch {
      showAlert({ severity: 'error', text: 'Ablehnen fehlgeschlagen.' });
    }
  };

  const handleCounter = async () => {
    if (!activeRequest || !counterStart) {
      return;
    }
    try {
      await counterMeetingRequest({
        idMeetingRequest: activeRequest.idLegalTransactionMeetingRequest,
        counterProposalStart: counterStart,
        ...(selectedRoom !== '' ? { idRoom: selectedRoom } : {}),
        staffMessage: staffMessage.trim() || undefined,
        subject: mailSubject,
        body: mailBody,
      });
      showAlert({ severity: 'success', text: 'Gegenvorschlag gesendet.' });
      closeDialog();
      void refetchMeetingRequests();
    } catch {
      showAlert({
        severity: 'error',
        text: 'Gegenvorschlag fehlgeschlagen (Slot evtl. nicht frei).',
      });
    }
  };

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Terminanfragen
      </Typography>

      {isLoadingMeetingRequests && <CircularProgress size={24} />}

      {!isLoadingMeetingRequests && meetingRequests.length === 0 && (
        <Typography color='text.secondary'>
          Keine Terminanfragen zu diesem Vorgang.
        </Typography>
      )}

      <Stack spacing={2}>
        {meetingRequests.map(request => {
          const info = STATUS_INFO[request.Status] ?? {
            label: 'Unbekannt',
            color: 'default',
          };
          const isOpen = request.Status === MEETING_REQUEST_STATUS.REQUESTED;
          const isCounter =
            request.Status === MEETING_REQUEST_STATUS.COUNTER_PROPOSED;

          return (
            <Card
              key={request.idLegalTransactionMeetingRequest}
              variant='outlined'
            >
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent='space-between'
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                >
                  <Box>
                    <Typography variant='subtitle1'>
                      Gewünscht: {formatDateTime(request.RequestedStart)}
                      {request.DurationInMin
                        ? ` (${request.DurationInMin} Min.)`
                        : ''}
                    </Typography>
                    {isCounter && (
                      <Typography variant='body2' color='info.main'>
                        Vorgeschlagen:{' '}
                        {formatDateTime(request.CounterProposalStart)}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={info.label}
                    color={info.color === 'default' ? 'default' : info.color}
                    variant='outlined'
                    size='small'
                  />
                </Stack>

                {locationName(request.idLocation) && (
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    <strong>Standort:</strong>{' '}
                    {locationName(request.idLocation)}
                  </Typography>
                )}
                {roomName(request.idRoom) && (
                  <Typography variant='body2' sx={{ mt: 0.5 }}>
                    <strong>Raum:</strong> {roomName(request.idRoom)}
                  </Typography>
                )}

                {clientName(request.idLegalClient) && (
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    <strong>Angefragt von:</strong>{' '}
                    {clientName(request.idLegalClient)}
                  </Typography>
                )}
                {request.Status === MEETING_REQUEST_STATUS.ACCEPTED &&
                  request.Decided_at && (
                    <Typography variant='body2' sx={{ mt: 0.5 }}>
                      <strong>Angenommen am:</strong>{' '}
                      {formatDateTime(request.Decided_at)}
                    </Typography>
                  )}

                {request.ClientMessage && (
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    <strong>Nachricht des Mandanten:</strong>{' '}
                    {request.ClientMessage}
                  </Typography>
                )}
                {request.StaffMessage && (
                  <Typography variant='body2' sx={{ mt: 0.5 }}>
                    <strong>Interne Notiz:</strong> {request.StaffMessage}
                  </Typography>
                )}

                {isOpen && (
                  <Stack direction='row' spacing={1} sx={{ mt: 2 }}>
                    <Button
                      variant='contained'
                      color='success'
                      size='small'
                      disabled={busy}
                      onClick={() => onAcceptClick(request)}
                    >
                      Annehmen
                    </Button>
                    <Button
                      variant='outlined'
                      color='error'
                      size='small'
                      disabled={busy}
                      onClick={() => openDialog('reject', request)}
                    >
                      Ablehnen
                    </Button>
                    <Button
                      variant='outlined'
                      size='small'
                      disabled={busy}
                      onClick={() => openDialog('counter', request)}
                    >
                      Gegenvorschlag
                    </Button>
                  </Stack>
                )}
                {isCounter && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Der Gegenvorschlag wartet auf die Rückmeldung des Mandanten.
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Alert
        severity={clientMeetingActive ? 'info' : 'warning'}
        variant='outlined'
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={clientMeetingActive}
              disabled={nothingBookable && !clientMeetingActive}
              onChange={e => onRequestClientMeetingChange?.(e.target.checked)}
            />
          }
          label='Mandant soll einen Termin vereinbaren'
        />
        <Typography variant='caption' color='text.secondary' display='block'>
          Aktiviert im Mandantenportal (Dashboard + Vorgang) einen
          Handlungsaufruf „Bitte vereinbaren Sie einen Termin". Wird erst beim{' '}
          <strong>Speichern des Vorgangs</strong> übernommen.
        </Typography>

        {nothingBookable && (
          <Alert severity='warning' sx={{ mt: 2 }}>
            Es sind keine buchbaren Zeiten konfiguriert. Bitte zuerst unter{' '}
            <strong>Buchbare Zeiten</strong> Zeitfenster anlegen — sonst kann
            kein Mandant einen Termin buchen.
          </Alert>
        )}

        {clientMeetingActive && !nothingBookable && legalClients.length > 0 && (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              Wer soll einen Termin vereinbaren?
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              Eine Terminart (z. B. Beurkundung, Erstberatung) legt Dauer,
              Standort, zuständigen Mitarbeiter und die buchbaren Zeiten fest.
              Die je Mandant wählbaren Terminarten stammen aus Einstellungen →
              Termine.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
                alignItems: 'start',
              }}
            >
              {legalClients.map(c => {
                const cfg = configByClient.get(c.idLegalClient);
                const selected = !!cfg;
                const name =
                  [c.FirstName, c.LastName].filter(Boolean).join(' ') ||
                  `#${c.idLegalClient}`;
                return (
                  <Box
                    key={c.idLegalClient}
                    sx={theme => ({
                      // Gleichmäßiges 2-Spalten-Raster (spart Platz); ausgewählte
                      // Mandanten wachsen einfach in ihrer Spalte (Konfig darunter).
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      backgroundColor: selected
                        ? alpha(theme.palette.primary.main, 0.04)
                        : 'transparent',
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 0.25,
                      transition: 'border-color .15s, background-color .15s',
                      '&:hover': {
                        borderColor: selected
                          ? 'primary.main'
                          : 'text.disabled',
                      },
                    })}
                  >
                    <FormControlLabel
                      sx={{ m: 0, width: '100%' }}
                      control={
                        <Checkbox
                          size='small'
                          checked={selected}
                          onChange={e =>
                            toggleClient(c.idLegalClient, e.target.checked)
                          }
                        />
                      }
                      label={
                        <Typography
                          variant='body2'
                          sx={{ fontWeight: selected ? 600 : 400 }}
                        >
                          {name}
                        </Typography>
                      }
                    />
                    {selected &&
                      (() => {
                        const typeLocs = bookableLocationsForType(
                          cfg?.idMeetingType ?? null
                        );
                        const presetTypeUnbookable =
                          cfg?.idMeetingType != null &&
                          !bookableTypeIds.has(cfg.idMeetingType);
                        const presetLocUnbookable =
                          cfg?.idLocation != null &&
                          !typeLocs.some(l => l.idLocation === cfg.idLocation);
                        return (
                          <Stack spacing={1.5} sx={{ mt: 1, pb: 1 }}>
                            {(presetTypeUnbookable || presetLocUnbookable) && (
                              <Alert severity='error'>
                                Diese Vorgabe hat keine buchbaren Zeiten — bitte
                                Terminart/Standort anpassen. Sonst lässt sich
                                der Vorgang nicht speichern.
                              </Alert>
                            )}
                            <FormControl size='small' fullWidth>
                              <InputLabel id={`mt-${c.idLegalClient}`}>
                                Terminart (optional)
                              </InputLabel>
                              <Select
                                labelId={`mt-${c.idLegalClient}`}
                                label='Terminart (optional)'
                                value={
                                  cfg?.idMeetingType == null
                                    ? ''
                                    : String(cfg.idMeetingType)
                                }
                                onChange={e => {
                                  const newType =
                                    e.target.value === ''
                                      ? null
                                      : Number(e.target.value);
                                  // Standort automatisch: genau ein buchbarer
                                  // Standort -> fix übernehmen; sonst zurücksetzen
                                  // (bei mehreren wählt Notar/Mandant, bei keinem
                                  // standortlos).
                                  const locs =
                                    bookableLocationsForType(newType);
                                  patchClient(c.idLegalClient, {
                                    idMeetingType: newType,
                                    idLocation:
                                      locs.length === 1
                                        ? locs[0].idLocation
                                        : null,
                                  });
                                }}
                              >
                                <MenuItem value=''>
                                  <em>Mandant wählt selbst</em>
                                </MenuItem>
                                {bookingOptions.map(o => (
                                  <MenuItem
                                    key={o.idMeetingType}
                                    value={String(o.idMeetingType)}
                                  >
                                    {o.Name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {/* Genau ein buchbarer Standort -> fest, keine Auswahl. */}
                            {cfg?.idMeetingType != null &&
                              typeLocs.length === 1 && (
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  <strong>Standort:</strong>{' '}
                                  {typeLocs[0].Name ?? 'Standort'} (fest)
                                </Typography>
                              )}
                            {/* Mehrere buchbare Standorte -> wählbar (leer = Mandant wählt). */}
                            {cfg?.idMeetingType != null &&
                              typeLocs.length > 1 && (
                                <FormControl size='small' fullWidth>
                                  <InputLabel id={`loc-${c.idLegalClient}`}>
                                    Standort (optional)
                                  </InputLabel>
                                  <Select
                                    labelId={`loc-${c.idLegalClient}`}
                                    label='Standort (optional)'
                                    value={
                                      cfg?.idLocation == null
                                        ? ''
                                        : String(cfg.idLocation)
                                    }
                                    onChange={e =>
                                      patchClient(c.idLegalClient, {
                                        idLocation:
                                          e.target.value === ''
                                            ? null
                                            : Number(e.target.value),
                                      })
                                    }
                                  >
                                    <MenuItem value=''>
                                      <em>Mandant wählt selbst</em>
                                    </MenuItem>
                                    {typeLocs.map(l => (
                                      <MenuItem
                                        key={l.idLocation}
                                        value={String(l.idLocation)}
                                      >
                                        {l.Name ?? 'Standort'}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            <TextField
                              label='Notiz an den Mandanten (optional)'
                              value={cfg?.note ?? ''}
                              onChange={e =>
                                patchClient(c.idLegalClient, {
                                  note: e.target.value,
                                })
                              }
                              fullWidth
                              size='small'
                              multiline
                              minRows={2}
                            />
                          </Stack>
                        );
                      })()}
                  </Box>
                );
              })}
            </Box>
            <FormHelperText>
              Ausgewählte Mandanten erhalten beim Speichern eine E-Mail und
              sehen die Aufforderung im Portal. Terminart/Standort vorgeben ist
              optional — sonst wählt der Mandant selbst.
            </FormHelperText>
          </Stack>
        )}
      </Alert>

      <Dialog
        open={dialogMode === 'accept'}
        onClose={closeDialog}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>
          Terminanfrage annehmen{acceptStep === 2 ? ' – E-Mail' : ''}
        </DialogTitle>
        <DialogContent>
          {conflictMessage && (
            <Alert severity='error' sx={{ mb: 1 }}>
              {conflictMessage}
            </Alert>
          )}
          {acceptStep === 1 && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant='body2'>
                <strong>Gewünschter Standort:</strong>{' '}
                {locationName(activeRequest?.idLocation) ?? '–'}
              </Typography>
              {roomsForActiveRequest.length === 0 ? (
                <Typography variant='body2' color='error'>
                  Für diesen Standort sind keine Räume hinterlegt. Bitte zuerst
                  im Standort einen Raum anlegen.
                </Typography>
              ) : (
                <FormControl fullWidth size='small' required>
                  <InputLabel id='accept-room-label'>Raum</InputLabel>
                  <Select
                    labelId='accept-room-label'
                    label='Raum'
                    value={selectedRoom === '' ? '' : String(selectedRoom)}
                    onChange={event =>
                      setSelectedRoom(Number(event.target.value))
                    }
                  >
                    {roomsForActiveRequest.map(room => (
                      <MenuItem key={room.idRoom} value={String(room.idRoom)}>
                        {room.Name}
                        {room.Capacity ? ` (${room.Capacity} Plätze)` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          )}
          {acceptStep === 2 && activeRequest && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <MeetingRequestMailEditor
                key={`accept-${activeRequest.idLegalTransactionMeetingRequest}`}
                idMeetingRequest={
                  activeRequest.idLegalTransactionMeetingRequest
                }
                action='accept'
                subject={mailSubject}
                onSubjectChange={setMailSubject}
                body={mailBody}
                onBodyChange={setMailBody}
              />
              {otherClients.length > 0 && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <FormLabel
                    sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                  >
                    Terminbestätigung auch an weitere Mandanten des Vorgangs?
                  </FormLabel>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 1 }}
                  >
                    Angehakte Mandanten nehmen an diesem Termin teil und
                    erhalten die Bestätigung inkl. Kalenderdatei (iCal). Ohne
                    Auswahl geht die Bestätigung nur an den anfragenden
                    Mandanten.
                  </Typography>
                  <FormGroup>
                    {otherClients.length > 1 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size='small'
                            checked={
                              sharedClientIds.length === otherClients.length
                            }
                            indeterminate={
                              sharedClientIds.length > 0 &&
                              sharedClientIds.length < otherClients.length
                            }
                            onChange={event =>
                              setSharedClientIds(
                                event.target.checked
                                  ? otherClients.map(c => c.idLegalClient)
                                  : []
                              )
                            }
                          />
                        }
                        label={<em>Alle auswählen</em>}
                      />
                    )}
                    {otherClients.map(c => (
                      <FormControlLabel
                        key={c.idLegalClient}
                        control={
                          <Checkbox
                            size='small'
                            checked={sharedClientIds.includes(c.idLegalClient)}
                            onChange={() => toggleSharedClient(c.idLegalClient)}
                          />
                        }
                        label={
                          clientName(c.idLegalClient) ?? `#${c.idLegalClient}`
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
              <TextField
                label='Interne Notiz (optional)'
                value={staffMessage}
                onChange={event => setStaffMessage(event.target.value)}
                fullWidth
                multiline
                minRows={2}
                helperText='Nur intern (am Vorgang gespeichert) — erscheint NICHT in der Mandanten-Mail.'
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Abbrechen</Button>
          {acceptStep === 1 && (
            <Button
              variant='contained'
              color='success'
              disabled={selectedRoom === ''}
              onClick={() => setAcceptStep(2)}
            >
              Weiter
            </Button>
          )}
          {acceptStep === 2 && (
            <>
              {activeRequest?.idLocation && (
                <Button onClick={() => setAcceptStep(1)}>Zurück</Button>
              )}
              <Button
                variant='contained'
                color='success'
                disabled={isAccepting}
                onClick={() => void handleAcceptWithRoom()}
              >
                Annehmen &amp; senden
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogMode === 'reject'}
        onClose={closeDialog}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>Terminanfrage ablehnen</DialogTitle>
        <DialogContent>
          {activeRequest && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <MeetingRequestMailEditor
                key={`reject-${activeRequest.idLegalTransactionMeetingRequest}`}
                idMeetingRequest={
                  activeRequest.idLegalTransactionMeetingRequest
                }
                action='reject'
                subject={mailSubject}
                onSubjectChange={setMailSubject}
                body={mailBody}
                onBodyChange={setMailBody}
              />
              <TextField
                label='Interne Notiz (optional)'
                value={staffMessage}
                onChange={event => setStaffMessage(event.target.value)}
                fullWidth
                multiline
                minRows={2}
                helperText='Nur intern (am Vorgang gespeichert) — erscheint NICHT in der Mandanten-Mail.'
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Abbrechen</Button>
          <Button
            variant='contained'
            color='error'
            disabled={isRejecting}
            onClick={() => void handleReject()}
          >
            Ablehnen &amp; senden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogMode === 'counter'}
        onClose={closeDialog}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>Gegenvorschlag machen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Raumwahl (wenn der Vorgang einen Standort hat): der Raum wird am
                Request gespeichert und beim Annehmen des Gegenvorschlags fest
                gebucht + auf Verfügbarkeit geprüft. */}
            {roomsForActiveRequest.length > 0 && (
              <FormControl fullWidth size='small'>
                <InputLabel id='counter-room-label'>Raum</InputLabel>
                <Select
                  labelId='counter-room-label'
                  label='Raum'
                  value={selectedRoom === '' ? '' : String(selectedRoom)}
                  onChange={event => {
                    setSelectedRoom(Number(event.target.value));
                    // Slot-Auswahl zurücksetzen: Slots sind raumabhängig.
                    setCounterStart(null);
                  }}
                >
                  {roomsForActiveRequest.map(room => (
                    <MenuItem key={room.idRoom} value={String(room.idRoom)}>
                      {room.Name}
                      {room.Capacity ? ` (${room.Capacity} Plätze)` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {activeRequest?.idMeetingType ? (
              // Freie Slots aus dem Belegungsplan wählen -> garantiert frei,
              // kein 409. Fallback (kein Terminart) unten: freier Datums-Picker.
              <CounterProposalSlotPicker
                key={`slots-${activeRequest.idLegalTransactionMeetingRequest}-${selectedRoom}`}
                idMeetingType={activeRequest.idMeetingType}
                idLocation={activeRequest.idLocation}
                idRoom={selectedRoom === '' ? undefined : selectedRoom}
                idUserCalendarConnection={
                  activeRequest.idUserCalendarConnection
                }
                durationInMin={activeRequest.DurationInMin}
                value={counterStart}
                onChange={setCounterStart}
              />
            ) : (
              <CustomeDateTimePicker
                dateValue={counterStart}
                setDateValue={setCounterStart}
                allowNull={false}
                allowPast={false}
                label='Vorgeschlagener Termin'
              />
            )}
            {activeRequest && (
              <MeetingRequestMailEditor
                key={`counter-${activeRequest.idLegalTransactionMeetingRequest}`}
                idMeetingRequest={
                  activeRequest.idLegalTransactionMeetingRequest
                }
                action='counter'
                subject={mailSubject}
                onSubjectChange={setMailSubject}
                body={mailBody}
                onBodyChange={setMailBody}
              />
            )}
            <TextField
              label='Interne Notiz (optional)'
              value={staffMessage}
              onChange={event => setStaffMessage(event.target.value)}
              fullWidth
              multiline
              minRows={2}
              helperText='Nur intern (am Vorgang gespeichert) — erscheint NICHT in der Mandanten-Mail.'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Abbrechen</Button>
          <Button
            variant='contained'
            disabled={isCountering || !counterStart}
            onClick={() => void handleCounter()}
          >
            Gegenvorschlag senden
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LegalTransactionMeetingRequestPanel;
